<template>
    <div>

        <form class="needs-validation" novalidate>
            <h1>{{$t('seb-title')}}</h1>
            
            <h2 class="mb-4">{{$t('Step 4 of 5: Confirm your information')}}</h2>


            <h3 class="mb-3">{{$t('Voter declaration and oath')}}</h3>
            <p>{{$t('To be eligible to vote by Sealed Envelope Ballot (SEB), all of the following must apply:')}}</p>
            <p>
                <i class="fas fa-check" aria-hidden="true"></i>
                {{$t('I am an eligible voter in this election but am unable to attend the voting place in person on election day or at advance voting opportunities for one of the following reasons:')}}
            </p>
            <ul>
                <li>{{$t('I am unable to go in person to a voting station due to a disability; or')}}</li>
                <li>{{$t('I am providing care to a person who is unable to leave their home; or')}}</li>
                <li>
                    {{$t('I expect that on Election Day and the day or days of Advance Voting, I will be:')}}
                    <ul>
                        <li>{{$t('absent from my local authority, or')}}</li>
                        <li>{{$t('at a location so significantly distant from the location I am eligible to vote at that it is not reasonably possible to vote at that location.')}}</li>
                    </ul>
                </li>
            </ul>
            <p>
                <i class="fas fa-check" aria-hidden="true"></i>
                {{$t('I have not otherwise already voted in this election.')}}
            </p>
            <p>
                <i class="fas fa-check" aria-hidden="true"></i>
                {{$t('I understand that upon receiving the Sealed Envelope Ballot package, I may only cast my ballot using this method of voting.')}}
            </p>

            <h3 class="mb-3">{{$t('Voter list information:')}}</h3>
            <p class="mb-2"><span class="font-weight-bold">{{$t('first_name')}}{{$t("colon")}}</span> {{ first_name }} </p>
            <p class="mb-2"><span class="font-weight-bold">{{$t('last_name')}}{{$t("colon")}}</span> {{ last_name }} </p>
            <p class="mb-2"><span class="font-weight-bold">{{$t('Address')}}{{$t("colon")}}</span> {{ address }}</p>
            <p class="mb-2"><span class="font-weight-bold">{{$t('date_of_birth')}}{{$t("colon")}}</span> {{ birth_date }}</p>

            <h3 class="mb-3">{{$t('Attached files:')}}</h3>
            <p class="mb-2"><span class="font-weight-bold">{{$t('File name')}}{{$t("colon")}}</span> <img :src="image" /><br />{{ image_name }} </p>

            <h3 class="mb-3">{{$t('SEB package mailing address:')}}</h3>
            <p class="mb-2"><span class="font-weight-bold">{{$t('Address')}}{{$t("colon")}}</span> {{ address1 }}</p>
            <p class="mb-2"><span class="font-weight-bold">{{$t('Address 2')}}{{$t("colon")}}</span> {{ address2 }}</p>
            <p class="mb-2"><span class="font-weight-bold">{{$t('City')}}{{$t("colon")}}</span> {{ city }}</p>
            <p class="mb-2"><span class="font-weight-bold">{{$t('Province, state, or region')}}{{$t("colon")}}</span> {{ province }}</p>
            <p class="mb-2"><span class="font-weight-bold">{{$t('Postal or zip code')}}{{$t("colon")}}</span> {{ postal_code }}</p>
            <p class="mb-2"><span class="font-weight-bold">{{$t('Country')}}{{$t("colon")}}</span> {{ country }}</p>

            <p class="mt-4"></p>

            <div class="custom-control custom-checkbox mt-5">
                <input type="checkbox" class="custom-control-input" id="confirmation" required v-model="confirmed">
                <label class="custom-control-label font-weight-normal" for="confirmation" v-html="$t('i_confirm')"></label>
                <div class="invalid-feedback" v-if="!valid_confirmation">{{$t('mandatory_requirement')}}</div>
            </div>

            <div class="row mt-4">
                <div class="col-md-6">
                    <div class="form-group mb-4">
                        <label for="voterFullName">{{$t('Full name')}}</label>
                        <input type="text" class="form-control" id="voterFullName" v-model="signature" aria-describedby="fullNameHelper" required>
                        <small id="fullNameHelper" class="form-text text-muted">
                            {{$t('Typing in your full name will be considered your electronic signature.')}}
                        </small>
                        <div class="invalid-feedback" v-if="valid_signature">{{$t('mandatory_requirement')}}</div>
                    </div>
                    <div class="form-group mb-4">
                        <label for="email">{{$t('email')}}</label>
                        <input id="email" type="email" class="form-control" v-model="contact_email" aria-describedby="contactEmailHelper" required>
                        <small id="contactEmailHelper" class="form-text text-muted">
                            {{$t('Provide an email address to receive your confirmation or should we need to contact you regarding any issues with your application.')}}
                        </small>
                        <div class="invalid-feedback" v-if="valid_email">{{$t('mandatory_requirement')}}</div>
                    </div>
                    <div class="form-group mb-4">
                        <label for="phoneNumber">{{$t('phone_number')}}</label>
                        <input id="phoneNumber" type="tel" class="form-control" pattern="[0-9]{3}[ -][0-9]{3}[ -][0-9]{4}" v-model="phone" @input="formatPhone" required>
                        <small class="form-text text-muted">
                            000-000-0000
                            <br />
                            {{$t('Provide a phone number should we need to contact you regarding any issues with your application.')}}
                        </small>
                        <div class="invalid-feedback" v-if="validate_phone">{{$t('valid_phone')}}</div>
                    </div>
                </div>
            </div>

            <div class="callout callout-info">
                <h3 class="mb-3">{{$t('Additional information')}}</h3>
                <ul>
                    <li>{{$t("For applications received by Friday, October 14, 2022, voters will receive a sealed envelope ballot package by mail, from the City Clerk's Department.")}}</li>
                    <li>{{$t("For applications received after Friday, October 14, 2022, voters must make alternative arrangements with the City Clerk's Department to receive a sealed envelope ballot package.")}}</li>
                    <li>{{$t("The voter must return the ballot to the Senior Election Official, City Clerk's Department, Susan A. Thompson Building, 510 Main Street, prior to 8:00 p.m. on Wednesday, October 26, 2022. The ballot must be returned to the Senior Election Official and will not be accepted at any voting location.")}}</li>
                </ul>
            </div>

            <div class="row no-gutters">
                <vue-recaptcha ref="recaptcha" @verify="onVerify" :sitekey="$CAPTCHA_KEY" :loadRecaptchaScript="true" :language="language"></vue-recaptcha>
                <div class="invalid-feedback" v-if="is_robot">
                    {{$t('Please verify that you are not a robot.')}}
                </div>
            </div>
            <div v-if="error_posting" class="callout callout-warning">
                <p>
                    {{$t('error_posting')}}
                </p>
            </div>
            <div class="row no-gutters" v-if="!seb_registry_closed">
                <div class="col"><button class="btn btn-outline-secondary mt-4 float-right" @click="prev_step">{{$t('previous')}}</button></div>
                <div class="col-auto"><button id="confirmButton" :disabled="confirm_button" class="btn btn-secondary mt-4 ml-3" @click="next_step">{{$t(next_button_text)}}</button></div>
            </div>
            <div class="row no-gutters" v-if="seb_registry_closed">
                <p>{{$t('seb_registration_close')}}</p>
            </div>
        </form>
    </div>
</template>
<style>
    a:hover {
        cursor: pointer;
    }
</style>
<script>
    import $ from "jquery";
    import axios from 'axios';
    import VueRecaptcha from 'vue-recaptcha';
    import axiosRetry from 'axios-retry';

    export default {
        components: { VueRecaptcha },

        data() {
            return {
                confirmButton: "confirm",
                waitButton: 'Please wait....',
                button_click: false,
                language: "en",
                eligible_to_vote: false,
                have_not_vote: false,
                only_vote_by_seb: false,
                personal_info: [],
                errors: [],
                seb_mailing_info: [],
                upload_info: [],
                voter: [],
                url: this.$APP_API_URL,
                mail_url: this.$MAIL_API_URL,
                error_mail_url: this.$ERROR_MAIL_API_URL,
                error_mail_url_2: this.$ERROR_MAIL_API_URL_2,
                is_robot: true,
                confirmed: false,
                response: {},
                registration_id: null,
                signature: null,
                contact_email: null,
                phone: "",
                identificationImage: null,
                seb_data: {
                    mailingAddress: "123 Home St",
                    contactInfo: "Contact Info",
                    comments: "Comments!",
                    identificationImage: null,
                    signature: null,
                    phoneNumber: null,
                    action: 'sealed-envelope-ballot'
                },
                mail_data: {
                    toEmailAddress: null,
                    isFrench: false
                },
                error_posting: false,
                email_error_sent: false,
                seb_registry_closed: false,
            }
        },

        computed: {
            first_name: function () {
                let result = "";
                if (this.personal_info.length > 0) {
                    result = this.personal_info[0].first_name;
                }

                return result;
            },
            last_name: function () {
                let result = "";
                if (this.personal_info.length > 0) {
                    result = this.personal_info[0].last_name;
                }

                return result;
            },
            address: function () {
                let result = "";
                if (this.personal_info.length > 0){
                    let info = this.personal_info[0];

                    if(typeof(info.street_number) === "undefined" )
                        result = info.address;
                    else
                        result = info.street_number + " " + info.street_name + " " + info.street_type;

                }

                return result;

            },
            birth_date: function () {
                let result = "";

                if (this.personal_info.length > 0)
                    result = this.personal_info[0].birth_year + '-' + ('0' + this.personal_info[0].birth_month).slice(-2) + '-' + ('0' + this.personal_info[0].birth_day).slice(-2);

                return result;
            },
            image: function () {
                let result = "";

                if (this.upload_info.length > 0)
                    result = this.upload_info[0].image;

                return result;
            },
            image_name: function () {
                let result = "";

                if (this.upload_info.length > 0)
                    result = this.upload_info[0].image_name;

                return result;
            },
            address1: function () {
                let result = "";

                if (this.seb_mailing_info.length > 0)
                    result = this.seb_mailing_info[0].address1;

                return result;
            },
            address2: function () {
                let result = "";

                if (this.seb_mailing_info.length > 0)
                    result = this.seb_mailing_info[0].address2;

                return result;
            },
            city: function () {
                let result = "";

                if (this.seb_mailing_info.length > 0)
                    result = this.seb_mailing_info[0].city;

                return result;
            },
            province: function () {
                let result = "";

                if (this.seb_mailing_info.length > 0)
                    result = this.seb_mailing_info[0].province;

                return result;
            },
            postal_code: function () {
                let result = "";

                if (this.seb_mailing_info.length > 0)
                    result = this.seb_mailing_info[0].postal_code;

                return result;
            },
            country: function () {
                let result = "";

                if (this.seb_mailing_info.length > 0)
                    result = this.seb_mailing_info[0].country;

                return result;
            },

            disabled_submit() {
                return !(!this.is_robot && this.confirmed && this.signature && this.contact_email);
            },

            valid_register() {
                return (!this.is_robot && this.confirmed && this.signature && this.contact_email && this.phone);
            },
            valid_confirmation() {
                return (this.confirmed);
            },
            valid_email() {
                return (this.contact_email === "" || this.contact_email === null);
            },
            valid_signature() {
                return (this.signature === null || this.signature === "");
            },
            validate_phone: function () {
                return ((this.phone === null) || this.phone === "");
            },
            next_button_text() {
                var result = (this.button_click ? this.waitButton : this.confirmButton);
                
                return result;
            },
            confirm_button() {
                return this.button_click;
            },
        },

        created: async function () {
            this.personal_info = this.$store.getters.PERSONAL_INFO;
            this.seb_mailing_info = this.$store.getters.SEB_MAILING_INFO;
            this.upload_info = this.$store.getters.UPLOAD_INFO;
            this.voter = this.$store.getters.VOTER;

            this.voter = this.$store.getters.SEB_VOTER;

            if (this.voter.length > 0) {
                let info = this.voter[0];

                this.eligible_to_vote = info.eligible_to_vote;
                this.have_not_vote = info.have_not_vote;
                this.only_vote_by_seb = info.only_vote_by_seb;
            }

            let voter_id = this.$route.params.voter_id;
            let registration_id = this.$route.params.registration_id;
            
            if (typeof (voter_id) !== "undefined" && voter_id !== 0)
                this.seb_data.voterId = voter_id;

            if (typeof(registration_id) !== "undefined" && voter_id === 0)
                this.seb_data.voterRegistrationId = registration_id;
           
            let mailingAddress = "";
            if (this.seb_mailing_info.length > 0) {
                let info = this.seb_mailing_info[0];

                mailingAddress = info.address1;
                if (typeof(info.address2) !== "undefined" && info.address2 !== null)
                    mailingAddress += " " + info.address2;

                mailingAddress += " " + info.city + " " + info.province;

                if (typeof(info.postal_code) !== "undefined")
                    mailingAddress += " " + info.postal_code;

                mailingAddress += " " + info.country;
            }
            this.seb_data.mailingAddress = mailingAddress;

            let contactInfo = "";
            if (this.personal_info.length > 0) {
                var personalInfo = this.personal_info[0];

                contactInfo = personalInfo.first_name + " " + personalInfo.last_name;
            }
            this.seb_data.contactInfo = contactInfo;

            let image = ( this.upload_info.length > 0 ? this.upload_info[0].actual_image : "");
            this.seb_data.identificationImage = image;
            this.identificationImage = image;

            this.language = process.env.VUE_APP_I18N_LOCALE;

            let url = this.url.replace("ElectionApplication.asp", "SEBDateValidation.ashx");

            await axios.get(url).then((result) => {
                this.seb_registry_closed = eval(result.data.toLowerCase());
            })
        },

        methods: {
            formatPhone() {
                var x = this.phone.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
                this.phone = !x[2] ? x[1] : x[1] + '-' + x[2] + (x[3] ? '-' + x[3] : '');
            },
            onVerify(response) {
                if (response) {
                    this.is_robot = false;
                }
            },            

            async next_step(event) {
                event.preventDefault();
                event.stopPropagation();

                this.error_posting = false;

                var params = new URLSearchParams();

                // Fetch all the forms we want to apply custom Bootstrap validation styles to
                var forms = document.getElementsByClassName('needs-validation');
                // Loop over them and prevent submission
                var validation = false;

                Array.prototype.filter.call(forms, function (form) {
                    validation = form.checkValidity();

                    if (form.checkValidity() === false) {
                        event.preventDefault();
                        event.stopPropagation();
                    }
                    form.classList.add('was-validated');

                    $(".invalid-feedback").removeClass("invalid-feedback").addClass("invalid");
                });

                if(this.is_robot && validation === false ){
                    event.preventDefault();
                    event.stopPropagation();
                }

                if (validation === true) {
                    let self = this;

                    this.seb_data.contactInfo += " Email= " + this.contact_email + " Phone= " + this.phone;
                    this.seb_data.signature = this.signature;
                    this.seb_data.phoneNumber = this.phone;

                    for (var i in this.seb_data) {
                        if (Object.prototype.hasOwnProperty.call(this.seb_data, i)) {
                            params.append(i, this.seb_data[i]);
                        }
                    }

                    this.button_click = true;

                    axiosRetry(axios, {
                        retries: 5,
                        shouldResetTimeout: true,
                        retryCondition: () => true,
                        retryDelay: (retryCount) => {
                            console.log(`retry attempt: ${retryCount}`);
                            return retryCount * 2000; // time interval between retries
                        }
                    });

                    await axios.post(self.url, params)
                        .then((result) => {
                            console.log(result);
                            var language = process.env.VUE_APP_I18N_LOCALE;
                            var french = (language === "fr" ? "Y" : "N");

                            var url = self.mail_url + "?toEmailAddress=" + self.contact_email + "&isFrench=" + french;

                            axios.post(url)
                                .then((result) => {
                                    console.log(result);
                                })
                                .catch(function (error) {
                                    console.log(error);
                                });
                            self.$router.push({ name: "SealedEnvelopeBallotStep5" });                            
                        })
                        .catch(function (error) {
                            self.button_click = false;

                            if (!self.email_error_sent) {
                                let voter_id = self.$route.params.voter_id;
                                let registration_id = self.$route.params.registration_id;
                                let message = "Voter id = " + voter_id;

                                if (typeof (registration_id) !== "undefined" && voter_id === 0)
                                    message = "Registration id = " + registration_id;

                                var formData = new URLSearchParams();
                                formData.append("t", "1");
                                formData.append("e", message + "<br />" + error.response.status + " " + error.response.statusText);
                                
                                axios.post(self.error_mail_url, formData);

                                params.append("t", "1");

                                axios.post(self.error_mail_url_2, params);

                                self.email_error_sent = true;
                            }

                            self.error_posting = true;
                        })
                }
            },

            prev_step: function () {
                this.$router.push({ name: "SealedEnvelopeBallotStep3", params: { page_type: this.$route.params.page_type, voter_id: this.$route.params.voter_id } });
            }
        }
    }
</script>